/**
 * Shared Stripe helpers reused across Stripe payment flows (TDC and wallets such as Google Pay).
 *
 * This module is intentionally dependency-free and side-effect-free so any Stripe action
 * (`stripe.js`, `stripeGooglePay.js`, …) can reuse the same result-normalization contract without
 * coupling to a specific payment method.
 */

/**
 * Normalize a Stripe payment response into a hook-friendly contract.
 *
 * @param {Object} camelizedResponse - The camelized payment response.
 * @returns {{
 *   outcome: 'completed'|'pending'|'requires_client_action'|'failed',
 *   clientAction?: { type: 'stripe_next_action', clientSecret: string },
 *   errorReason?: string,
 *   raw: Object,
 * }}
 */
export function normalizeStripeResult(camelizedResponse = {}) {
  const { status, payload = {} } = camelizedResponse;
  const responseStatus = status || (camelizedResponse.polls ? 'pending' : undefined);
  const errorReason = payload.errorReason || 'default';

  if (payload.using3dSecure === true && payload.clientSecret) {
    return {
      outcome: 'requires_client_action',
      clientAction: {
        type: 'stripe_next_action',
        clientSecret: payload.clientSecret,
      },
      errorReason,
      raw: camelizedResponse,
    };
  }

  if (payload.purchaseState !== 'attempt' || payload.status === 'charged') {
    return {
      outcome: 'completed',
      errorReason,
      raw: camelizedResponse,
    };
  }

  if (
    !['pending', 'completed', 'aborted'].includes(responseStatus) ||
    ['failed', 'rejected'].includes(payload.status)
  ) {
    return {
      outcome: 'failed',
      errorReason,
      raw: camelizedResponse,
    };
  }

  return {
    outcome: 'pending',
    errorReason,
    raw: camelizedResponse,
  };
}

export default normalizeStripeResult;
